<template>
  <div class="mapping-rules-entry-field" :class="mainClasses">
    <!-- <UISwitch
      v-model="isMappedFromJson"
      label="Mapped from JSON"
      class="mapping-rules-entry-field__switch"
    /> -->
    <slot>
      <UIInputText
        v-model="inputValue"
        :data-refid
        :label
        :size
        :placeholder
        :readonly="isJsonAvailable"
        :prefix
        :disabled
        @click="handleOpenJSON"
        @ready="handleReady"
      />
    </slot>
    <CheckIcon
      v-if="isFieldSet"
      class="mapping-rules-entry-field__check"
      aria-hidden="true"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, markRaw, ref, watch } from 'vue'

import {
  InputSize,
  TransactionSettingsAsset,
  TransactionSettingsAmount,
} from '@types'
import { MappingRuleActionField } from '../utils/types'

import { useModalsStore } from '@/store/modals'

import JSONModal from './JSONModal.vue'

import { UIInputText } from '@ui/fields'
// import { UISwitch } from '@ui/controllers'
import { CheckIcon } from '@heroicons/vue/20/solid'

type Props = {
  label?: string
  settings?: TransactionSettingsAsset | TransactionSettingsAmount
  jsonData?: Record<string, unknown>
  disabled?: boolean
  dataRefid?: string
}

const props = defineProps<Props>()

const modelValue = defineModel<MappingRuleActionField>()

const size = inject<InputSize>('size')

const inputRef = ref<HTMLInputElement>()
const isFieldSet = ref(false)

const modalsStore = useModalsStore()

const label = computed(() => props.settings?.title || props.label)

const placeholder = computed(() =>
  isMappedFromJson.value
    ? `${isJsonAvailable.value ? 'Click to select' : 'Input'} JSON field path`
    : 'Input custom value',
)

const isJsonAvailable = computed(() => {
  return props.jsonData && modelValue.value?.isMappedFromJson
})

const inputValue = computed({
  get() {
    return modelValue.value?.value
  },
  set(value) {
    modelValue.value = {
      ...modelValue.value,
      value,
    }
  },
})

const prefix = computed(() => {
  return isMappedFromJson.value && inputValue.value ? 'JSON.' : undefined
})

const isMappedFromJson = computed({
  get() {
    return modelValue.value?.isMappedFromJson
  },
  set(isMappedFromJson) {
    modelValue.value = {
      ...modelValue.value,
      isMappedFromJson,
    }
  },
})

const mainClasses = computed(() => ({
  'mapping-rules-entry-field--set': isFieldSet.value,
}))

const handleOpenJSON = async () => {
  if (!isJsonAvailable.value) return

  const modalInstance = modalsStore.init('entry-field-json-data', markRaw(JSONModal))
  modalInstance?.open(modalsStore.getZIndex(), {
    json: props.jsonData,
    onClick: (path: string[]) => {
      inputValue.value = path.slice(1).join('.')
      isFieldSet.value = true
      setTimeout(() => {
        isFieldSet.value = false
      }, 750)
    },
  })
}

const handleReady = (e: HTMLInputElement) => {
  inputRef.value = e
}

watch(isMappedFromJson, mapped => {
  inputValue.value = undefined
  if (mapped) return
  inputRef.value?.focus()
})
</script>

<style>
@keyframes check-ping {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.25);
  }
  50% {
    transform: scale(1);
  }
  75% {
    transform: scale(1.25);
  }
}

.mapping-rules-entry-field {
  @apply flex items-end gap-2 relative;

  .ui-input-text {
    @apply flex-auto;
  }

  .ui-input-text__area {
    @apply pointer-events-auto;

    [readonly] {
      @apply cursor-pointer;
    }
  }

  .ui-input-text__prefix {
    @apply w-12;
    @apply text-sm sm:text-xs;
  }

  .ui-input-text__input--prefix {
    @apply pl-12;
  }

  .ui-switch {
    @apply absolute right-0 top-0;
    @apply z-10;
  }

  &__check {
    @apply size-4;
    @apply absolute right-2 bottom-2;
    @apply text-(--focus-border);
    @apply animate-[check-ping_500ms_ease-in-out_infinite];
  }
}
</style>
